import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { isMultiSeries, deriveSeries } from "./chartSeries";

const DEFAULT_HEIGHT = 320;

const PALETTE = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16", "#ef4444"];

const AXIS_STYLE = { fill: "#6b7280", fontSize: 12, fontFamily: "Inter" };
const GRID_STYLE = { stroke: "#f3f4f6" };

function formatNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return String(value);
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);
  }
  if (!Number.isInteger(value)) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
  }
  return String(value);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md">
      {label !== undefined && <div className="text-xs font-medium text-gray-700 mb-1">{label}</div>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-500">{entry.name}:</span>
          <span className="font-medium text-gray-900">{formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function ChartRenderer({ spec }) {
  const { chart_type, data, x_axis_label, y_axis_label } = spec;

  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-400 italic py-8 text-center">No data to display.</div>;
  }

  switch (chart_type) {
    case "kpi": {
      const row = data[0];
      const value = row.value;
      const formatted = formatNumber(value);
      const hasComparison = row.comparison_value !== undefined && row.comparison_value !== null;
      let delta = null;
      let deltaColor = "";
      if (hasComparison && typeof value === "number" && typeof row.comparison_value === "number") {
        const change = value - row.comparison_value;
        const pct = row.comparison_value !== 0 ? (change / row.comparison_value) * 100 : null;
        if (pct !== null) {
          delta = `${change >= 0 ? "+" : ""}${formatNumber(change)} (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)`;
          deltaColor = change > 0 ? "text-emerald-600" : change < 0 ? "text-red-600" : "text-gray-500";
        }
      }
      return (
        <div className="text-center py-4">
          <div className="text-5xl font-semibold text-gray-900 tracking-tight tabular-nums">{formatted}</div>
          {hasComparison && delta && (
            <div className={`text-sm font-medium mt-2 ${deltaColor}`}>
              {delta}
              {row.comparison_label && <span className="text-gray-400 font-normal ml-1.5">{row.comparison_label}</span>}
            </div>
          )}
        </div>
      );
    }

    case "bar":
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: x_axis_label ? 24 : 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false}
              label={x_axis_label ? { value: x_axis_label, position: "insideBottom", offset: -10, style: AXIS_STYLE } : undefined} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false}
              label={y_axis_label ? { value: y_axis_label, angle: -90, position: "insideLeft", style: AXIS_STYLE } : undefined}
              tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
            <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );

    case "line": {
      if (isMultiSeries(data)) {
        const { segments, dataset } = deriveSeries(data);
        return (
          <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
            <LineChart data={dataset} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
              <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
              {segments.map((seg, i) => (
                <Line key={seg} type="monotone" dataKey={seg} stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2} dot={{ fill: PALETTE[i % PALETTE.length], r: 3 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      }
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={{ fill: "#2563eb", r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    case "pie":
    case "donut":
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <PieChart>
            <Pie
              data={data} dataKey="value" nameKey="label"
              cx="50%" cy="50%"
              outerRadius={chart_type === "donut" ? 110 : 120}
              innerRadius={chart_type === "donut" ? 65 : 0}
              paddingAngle={1}
              labelLine={false}
              label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
            >
              {data.map((entry, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="white" strokeWidth={2} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
          </PieChart>
        </ResponsiveContainer>
      );

    case "stacked_bar": {
      const { segments, dataset } = deriveSeries(data);
      return (
        <ResponsiveContainer width="100%" height={DEFAULT_HEIGHT}>
          <BarChart data={dataset} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            <XAxis dataKey="label" tick={AXIS_STYLE} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
            <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Inter", paddingTop: 8 }} iconType="circle" iconSize={8} />
            {segments.map((seg, i) => (
              <Bar key={seg} dataKey={seg} stackId="a" fill={PALETTE[i % PALETTE.length]} radius={i === segments.length - 1 ? [4, 4, 0, 0] : 0} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }

    case "table": {
      const columns = Object.keys(data[0]);
      return (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                {columns.map((c) => (
                  <th key={c} className="px-3 py-2.5 text-left font-medium text-gray-700 text-xs uppercase tracking-wider">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                  {columns.map((c) => {
                    const val = row[c];
                    const display = val === null || val === undefined ? "—"
                                  : typeof val === "number" ? formatNumber(val)
                                  : typeof val === "object" ? JSON.stringify(val)
                                  : String(val);
                    return (
                      <td key={c} className={`px-3 py-2.5 text-gray-900 ${typeof val === "number" ? "tabular-nums text-right" : ""}`}>
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    default:
      return <div className="text-sm text-red-600">Unsupported chart type: {chart_type}</div>;
  }
}
